import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { LineChart } from '../components/charts';

const API = 'https://api.glowytics.com';

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (v >= 1_000) return `${(v / 1_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}K`;
  return v.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
};
const fmtCur = (n) => `₺${fmt(n)}`;
const fmtPct = (n) => `%${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
const fmtDec = (n) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney2 = (n) => `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const greeting = () => {
  const h = new Date().getHours();
  return h >= 5 && h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

const rangeLabel = (v) => {
  switch (v) {
    case 'last7':
      return 'Last 7 days';
    case 'last30':
      return 'Last 30 days';
    case 'thisMonth':
      return 'Month to date';
    case 'last90':
      return 'Last 90 days';
    default:
      return 'Selected period';
  }
};

const SegButton = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={[
      'px-3 py-1.5 text-[12px] font-medium rounded-[10px] transition-all',
      active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-white/60',
    ].join(' ')}
  >
    {children}
  </button>
);

const Metric = ({ label, value, accent = false, align = 'left' }) => (
  <div className="min-w-0">
    <div className={`text-[10px] uppercase tracking-widest text-slate-400 font-semibold ${align === 'right' ? 'text-right' : ''}`}>
      {label}
    </div>
    <div
      className={[
        'mt-1 text-[15px] font-bold tabular-nums',
        accent ? 'text-slate-900' : 'text-slate-800',
        align === 'right' ? 'text-right' : '',
      ].join(' ')}
    >
      {value}
    </div>
  </div>
);

const Surface = ({ children, className = '' }) => (
  <div className={`bg-white/70 backdrop-blur-sm rounded-3xl border border-white/80 shadow-sm ${className}`}>{children}</div>
);

const Divider = () => <div className="h-px bg-slate-200/60" />;

const ToneDot = ({ tone }) => (
  <span
    className={[
      'inline-block w-2 h-2 rounded-full',
      tone === 'good' ? 'bg-emerald-500' : tone === 'warn' ? 'bg-amber-500' : tone === 'bad' ? 'bg-rose-500' : 'bg-slate-300',
    ].join(' ')}
  />
);

const dateKeyOf = (row) => {
  const raw = (row?.date ?? row?.day ?? row?.segment_date ?? row?.report_date ?? row?.month ?? '').toString();
  return raw.includes('T') ? raw.split('T')[0] : raw;
};

export default function TestingOverview2() {
  const { user } = useAuth();
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

  const [dateRange, setDateRange] = useState('last7');
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [liveUpdatedAt, setLiveUpdatedAt] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveGads, setLiveGads] = useState(null);
  const [liveMeta, setLiveMeta] = useState(null);
  const [liveGa4, setLiveGa4] = useState(null);

  const [perfLoading, setPerfLoading] = useState(false);
  const [googleAdsRows, setGoogleAdsRows] = useState([]);
  const [metaAdsRows, setMetaAdsRows] = useState([]);

  const [driversLoading, setDriversLoading] = useState(false);
  const [trafficRows, setTrafficRows] = useState([]);
  const [eventsRows, setEventsRows] = useState([]);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [gaDailyRows, setGaDailyRows] = useState([]);

  const [liveOpen, setLiveOpen] = useState(false);

  const mkHeaders = useCallback(() => ({ 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }), [brandId]);
  const get = useCallback(
    async (url) => {
      try {
        const r = await fetch(url, { credentials: 'include', headers: mkHeaders() });
        if (!r.ok) return null;
        return await r.json();
      } catch {
        return null;
      }
    },
    [mkHeaders]
  );

  useEffect(() => {
    if (!brandId) return;
    get(`${API}/api/brand-info/${brandId}/details`).then((d) => {
      if (d?.logoUrl) setLogoUrl(d.logoUrl);
    });
  }, [brandId, get]);

  const fetchGaDaily = useCallback(async () => {
    if (!brandId) return;
    const d = await get(`${API}/api/google-ads/ga-summary-daily?dateRange=all`);
    const rows = Array.isArray(d?.data) ? d.data : (Array.isArray(d) ? d : []);
    setGaDailyRows(rows);
  }, [brandId, get]);

  const agg = useCallback((rows, costKey = 'cost') => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    return rows.reduce(
      (a, r) => ({
        cost: a.cost + Number(r[costKey] || r.cost || r.spend || r.adsCost || 0),
        clicks: a.clicks + Number(r.clicks || r.adsClicks || 0),
        impressions: a.impressions + Number(r.impressions || r.adsImpressions || 0),
        conversions: a.conversions + Number(r.conversions || r.adsConversions || 0),
        adsRevenue:
          a.adsRevenue +
          Number(r.conversion_value || r.conversionValue || r.adsConversionValue || r.adsRevenue || 0),
        sessions: a.sessions + Number(r.ga4_sessions || r.sessions || r.gaSessions || 0),
        transactions: a.transactions + Number(r.ga4_transactions || r.transactions || r.gaTransactions || 0),
        revenue: a.revenue + Number(r.ga4_total_revenue || r.revenue || r.totalRevenue || r.gaRevenue || 0),
      }),
      { cost: 0, clicks: 0, impressions: 0, conversions: 0, adsRevenue: 0, sessions: 0, transactions: 0, revenue: 0 }
    );
  }, []);

  const gAgg = useMemo(() => agg(googleAdsRows, 'cost'), [agg, googleAdsRows]);
  const mAgg = useMemo(() => agg(metaAdsRows, 'spend'), [agg, metaAdsRows]);

  const totalKpi = useMemo(() => {
    const g = gAgg || {};
    const m = mAgg || {};
    const spend = (g.cost || 0) + (m.cost || 0);
    const rev = (g.revenue || 0) + (m.revenue || 0);
    const ses = (g.sessions || 0) + (m.sessions || 0);
    const tx = (g.transactions || 0) + (m.transactions || 0);
    return {
      spend,
      rev,
      ses,
      tx,
      roas: spend > 0 ? rev / spend : 0,
      cr: ses > 0 ? (tx / ses) * 100 : 0,
      aov: tx > 0 ? rev / tx : 0,
    };
  }, [gAgg, mAgg]);

  const fetchPerf = useCallback(async () => {
    if (!brandId) return;
    setPerfLoading(true);
    const [gData, mData] = await Promise.all([
      get(`${API}/api/google-ads/unified-googleads-ga4-performance?dateRange=${dateRange}`),
      get(`${API}/api/google-ads/unified-meta-ga4-performance?dateRange=${dateRange}`),
    ]);
    setGoogleAdsRows(Array.isArray(gData) ? gData : (gData?.data || gData?.dailyData || []));
    setMetaAdsRows(Array.isArray(mData) ? mData : (mData?.data || mData?.dailyData || []));
    setPerfLoading(false);
  }, [brandId, dateRange, get]);

  const fetchLive = useCallback(async () => {
    if (!brandId) return;
    setLiveLoading(true);
    const [g, m, a] = await Promise.allSettled([
      get(`${API}/api/brands/${brandId}/live/google-ads/today`),
      get(`${API}/api/brands/${brandId}/live/meta-ads/today`),
      get(`${API}/api/brands/${brandId}/live/ga4/today`),
    ]);
    setLiveGads(g.status === 'fulfilled' ? g.value : null);
    setLiveMeta(m.status === 'fulfilled' ? m.value : null);
    setLiveGa4(a.status === 'fulfilled' ? a.value : null);
    setLiveUpdatedAt(new Date());
    setLiveLoading(false);
  }, [brandId, get]);

  const fetchTraffic = useCallback(async () => {
    if (!brandId) return;
    const d = await get(`${API}/api/google-ads/ga-traffic-source?dateRange=${dateRange}`);
    setTrafficRows(d?.detailedData || (Array.isArray(d) ? d : []));
  }, [brandId, dateRange, get]);

  const fetchEvents = useCallback(async () => {
    if (!brandId) return;
    const d = await get(`${API}/api/google-ads/ga-events-raw?dateRange=${dateRange}`);
    setEventsRows(Array.isArray(d) ? d : (d?.data || d?.rows || []));
  }, [brandId, dateRange, get]);

  const fetchMonthly = useCallback(async () => {
    if (!brandId) return;
    const d = await get(`${API}/api/google-ads/ga-summary-monthly?dateRange=all`);
    setMonthlyRows(Array.isArray(d?.data) ? d.data : (Array.isArray(d) ? d : []));
  }, [brandId, get]);

  useEffect(() => {
    if (!brandId) return;
    (async () => {
      setLoading(true);
      await Promise.all([fetchPerf(), fetchLive(), fetchTraffic(), fetchEvents(), fetchMonthly(), fetchGaDaily()]);
      setLoading(false);
    })();
  }, [brandId, fetchEvents, fetchGaDaily, fetchLive, fetchMonthly, fetchPerf, fetchTraffic]);

  useEffect(() => {
    if (!brandId) return;
    setDriversLoading(true);
    Promise.all([fetchPerf(), fetchTraffic(), fetchEvents()]).finally(() => setDriversLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, brandId]);

  const onRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchPerf(), fetchLive(), fetchTraffic(), fetchEvents(), fetchMonthly(), fetchGaDaily()]);
    setRefreshing(false);
  };

  const ranges = [
    { v: 'last7', l: '7D' },
    { v: 'last30', l: '30D' },
    { v: 'thisMonth', l: 'MTD' },
    { v: 'last90', l: '90D' },
  ];

  const adsSummary = useMemo(() => {
    const g = gAgg || {};
    const m = mAgg || {};
    const gRow = {
      platform: 'Google Ads',
      icon: '/google_ads-512.webp',
      sessions: Number(g.sessions || 0),
      clicks: Number(g.clicks || 0),
      cost: Number(g.cost || 0),
      conversions: Number(g.conversions || 0),
      ga4Revenue: Number(g.revenue || 0),
      revenue: Number(g.adsRevenue || 0),
    };
    const mRow = {
      platform: 'Meta Ads',
      icon: '/6033716.png',
      sessions: Number(m.sessions || 0),
      clicks: Number(m.clicks || 0),
      cost: Number(m.cost || 0),
      conversions: Number(m.conversions || 0),
      ga4Revenue: Number(m.revenue || 0),
      revenue: Number(m.adsRevenue || 0),
    };
    const total = {
      sessions: gRow.sessions + mRow.sessions,
      clicks: gRow.clicks + mRow.clicks,
      cost: gRow.cost + mRow.cost,
      conversions: gRow.conversions + mRow.conversions,
      ga4Revenue: gRow.ga4Revenue + mRow.ga4Revenue,
      revenue: gRow.revenue + mRow.revenue,
    };
    const cpc = total.clicks > 0 ? total.cost / total.clicks : 0;
    const cps = total.sessions > 0 ? total.cost / total.sessions : 0;
    return {
      rows: [
        { ...gRow, cpc: gRow.clicks > 0 ? gRow.cost / gRow.clicks : 0, cps: gRow.sessions > 0 ? gRow.cost / gRow.sessions : 0 },
        { ...mRow, cpc: mRow.clicks > 0 ? mRow.cost / mRow.clicks : 0, cps: mRow.sessions > 0 ? mRow.cost / mRow.sessions : 0 },
      ],
      total: { ...total, cpc, cps },
    };
  }, [gAgg, mAgg]);

  const dailyPerformance = useMemo(() => {
    const dk = (row) => {
      const raw = (row?.date ?? row?.day ?? row?.segment_date ?? row?.report_date ?? '').toString();
      return raw.includes('T') ? raw.split('T')[0] : raw;
    };

    const today = new Date();
    let start = new Date(today);
    let maxRows = 14;
    if (dateRange === 'last7') {
      start.setDate(today.getDate() - 6);
      maxRows = 7;
    } else if (dateRange === 'last30') {
      start.setDate(today.getDate() - 29);
      maxRows = 14;
    } else if (dateRange === 'last90') {
      start.setDate(today.getDate() - 89);
      maxRows = 14;
    } else if (dateRange === 'thisMonth') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      maxRows = Math.min(14, today.getDate());
    }

    const startKey = start.toISOString().slice(0, 10);
    const endKey = today.toISOString().slice(0, 10);

    const gaByDate = {};
    for (const r of gaDailyRows || []) {
      const k = dk(r);
      if (!k) continue;
      gaByDate[k] = r;
    }

    const gCostByDate = {};
    for (const r of googleAdsRows || []) {
      const k = dk(r);
      if (!k) continue;
      const c = Number(r.cost || 0);
      gCostByDate[k] = (gCostByDate[k] || 0) + (isFinite(c) ? c : 0);
    }

    const mCostByDate = {};
    for (const r of metaAdsRows || []) {
      const k = dk(r);
      if (!k) continue;
      const c = Number(r.spend || 0);
      mCostByDate[k] = (mCostByDate[k] || 0) + (isFinite(c) ? c : 0);
    }

    // Build date list descending, but only show a compact slice.
    const rows = [];
    const cur = new Date(today);
    while (cur.toISOString().slice(0, 10) >= startKey) {
      const k = cur.toISOString().slice(0, 10);
      const ga = gaByDate[k] || {};
      const sessions = Number(ga.sessions || ga.totalSessions || 0);
      const tx = Number(ga.transactions || ga.eventCount || ga.purchaseCount || 0);
      const revenue = Number(ga.totalRevenue || ga.revenue || ga.total_revenue || 0);
      const cr = sessions > 0 ? (tx / sessions) * 100 : 0;
      rows.push({
        date: k,
        sessions,
        transactions: tx,
        cr,
        revenue,
        gCost: Number(gCostByDate[k] || 0),
        mCost: Number(mCostByDate[k] || 0),
      });
      cur.setDate(cur.getDate() - 1);
      if (rows.length >= maxRows) break;
    }

    return { rows, windowLabel: `${rangeLabel(dateRange)} (showing ${rows.length} days)`, startKey, endKey };
  }, [dateRange, gaDailyRows, googleAdsRows, metaAdsRows]);

  const topSources = useMemo(() => {
    const map = {};
    for (const r of trafficRows || []) {
      const k = r.sessionSourceMedium || r.source_medium || `${r.source || 'direct'}/${r.medium || 'none'}`;
      if (!map[k]) map[k] = { source: k, sessions: 0, revenue: 0 };
      map[k].sessions += Number(r.sessions || 0);
      map[k].revenue += Number(r.totalRevenue || r.revenue || 0);
    }
    return Object.values(map)
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 4);
  }, [trafficRows]);

  const funnel = useMemo(() => {
    const em = {};
    for (const r of eventsRows || []) {
      const n = r.eventName || r.event_name || '';
      em[n] = (em[n] || 0) + Number(r.eventCount || r.event_count || 0);
    }
    const steps = [
      { label: 'View item', key: 'view_item' },
      { label: 'Add to cart', key: 'add_to_cart' },
      { label: 'Checkout', key: 'begin_checkout' },
      { label: 'Purchase', key: 'purchase' },
    ].map((s) => ({ ...s, value: em[s.key] || 0 }));

    const maxV = Math.max(...steps.map((s) => s.value), 1);
    return steps.map((s, i) => {
      const prev = i > 0 ? steps[i - 1].value : null;
      const drop = prev && prev > 0 ? ((prev - s.value) / prev) * 100 : 0;
      return { ...s, pctOfMax: (s.value / maxV) * 100, drop };
    });
  }, [eventsRows]);

  const channelCards = useMemo(() => {
    const g = gAgg || { cost: 0, revenue: 0, sessions: 0, transactions: 0 };
    const m = mAgg || { cost: 0, revenue: 0, sessions: 0, transactions: 0 };
    const totalRev = (g.revenue || 0) + (m.revenue || 0) || 1;
    const toCard = (name, x, toneBase) => {
      const spend = Number(x.cost || 0);
      const rev = Number(x.revenue || 0);
      const ses = Number(x.sessions || 0);
      const tx = Number(x.transactions || 0);
      const roas = spend > 0 ? rev / spend : 0;
      const cr = ses > 0 ? (tx / ses) * 100 : 0;
      const share = (rev / totalRev) * 100;
      const tone = spend > 0 && roas < 1 ? 'bad' : spend > 0 && roas < 2 ? 'warn' : 'good';
      return { name, spend, rev, roas, cr, share, tone, toneBase };
    };
    return [toCard('Google Ads', g, 'from-blue-500 to-indigo-600'), toCard('Meta Ads', m, 'from-orange-500 to-amber-600')];
  }, [gAgg, mAgg]);

  const trendData = useMemo(() => {
    if (!Array.isArray(monthlyRows) || monthlyRows.length === 0) return null;
    const sorted = [...monthlyRows]
      .sort((a, b) => (a.month || a.date || '').localeCompare(b.month || b.date || ''))
      .slice(-12);
    return {
      labels: sorted.map((r) => {
        const d = new Date((r.month || r.date) + '-01');
        return d.toLocaleDateString('en-US', { month: 'short' });
      }),
      datasets: [
        {
          label: 'Revenue',
          data: sorted.map((r) => Number(r.totalRevenue || r.revenue || r.total_revenue || 0)),
          borderColor: '#0f172a',
          backgroundColor: 'transparent',
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    };
  }, [monthlyRows]);

  const trendOpts = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: {
        x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } },
        y: { display: false, grid: { display: false } },
      },
    }),
    []
  );

  const insightCards = useMemo(() => {
    const items = [];
    const spend = Number(totalKpi.spend || 0);
    const rev = Number(totalKpi.rev || 0);
    const roas = Number(totalKpi.roas || 0);
    const cr = Number(totalKpi.cr || 0);

    if (spend > 0 && roas < 1) {
      items.push({
        tone: 'bad',
        title: 'ROAS is under 1.0x',
        body: 'You are spending more than you earn in this period. Review budgets and landing quality.',
      });
    } else if (spend > 0 && roas < 2) {
      items.push({
        tone: 'warn',
        title: 'Efficiency can improve',
        body: 'ROAS is below 2.0x. Consider shifting budget to better performing campaigns.',
      });
    } else if (rev > 0) {
      items.push({
        tone: 'good',
        title: 'Revenue is in a healthy range',
        body: 'Keep an eye on efficiency and funnel drop-offs to protect performance.',
      });
    }

    if (Number.isFinite(cr) && cr > 0 && cr < 1) {
      items.push({
        tone: 'warn',
        title: 'Conversion rate is low',
        body: 'If traffic is steady, check checkout friction, pricing, and site speed.',
      });
    } else if (cr >= 1 && cr < 2) {
      items.push({
        tone: 'neutral',
        title: 'Conversion is stable',
        body: 'Small UX or offer improvements can lift revenue without raising spend.',
      });
    }

    const totalSessions = topSources.reduce((s, x) => s + Number(x.sessions || 0), 0) || 0;
    const lead = topSources[0];
    if (lead && totalSessions > 0 && lead.sessions / totalSessions > 0.6) {
      items.push({
        tone: 'warn',
        title: 'Traffic is concentrated',
        body: `${lead.source} drives most sessions in the top sources. Diversifying can reduce risk.`,
      });
    } else if (topSources.length > 0) {
      items.push({
        tone: 'neutral',
        title: 'Top traffic sources',
        body: `Top sources are ${topSources
          .slice(0, 2)
          .map((s) => s.source)
          .join(' and ')}. Watch changes when you adjust budgets.`,
      });
    }

    while (items.length < 3) {
      items.push({
        tone: 'neutral',
        title: 'Keep it simple',
        body: 'Start with one goal for the week, then let drivers (channel + funnel) explain the outcome.',
      });
    }
    return items.slice(0, 3);
  }, [totalKpi, topSources]);

  if (!brandId) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="max-w-lg w-full bg-white/70 backdrop-blur rounded-2xl border border-slate-200 shadow-sm p-6">
          <div className="text-sm font-semibold text-slate-900">No brand selected</div>
          <div className="text-sm text-slate-600 mt-2">Select a brand first.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-[1120px] mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="w-10 h-10 rounded-2xl object-cover ring-2 ring-white shadow-sm" />
            ) : (
              <div className="w-10 h-10 rounded-2xl bg-white/70 border border-white/80 shadow-sm" />
            )}
            <div className="min-w-0">
              <div className="text-[11px] text-slate-400 font-semibold tracking-widest uppercase">{greeting()}</div>
              <div className="text-base font-semibold text-slate-900 truncate">{user?.name || 'User'}</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:inline-flex items-center bg-white/70 backdrop-blur rounded-xl border border-white/80 shadow-sm p-0.5">
              {ranges.map((r) => (
                <SegButton key={r.v} active={dateRange === r.v} onClick={() => setDateRange(r.v)}>
                  {r.l}
                </SegButton>
              ))}
            </div>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="w-10 h-10 rounded-xl bg-white/70 backdrop-blur border border-white/80 shadow-sm flex items-center justify-center hover:bg-white transition disabled:opacity-60"
              title="Refresh"
            >
              {refreshing ? <Loader2 className="w-4 h-4 text-slate-500 animate-spin" /> : <RefreshCw className="w-4 h-4 text-slate-500" />}
            </button>
          </div>
        </div>

        {/* Hero */}
        <div className="mb-8">
          <div className="text-[12px] text-slate-500">{rangeLabel(dateRange)} · {liveUpdatedAt ? `Updated ${liveUpdatedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : ' '}</div>
          <div className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Performance</div>
          <div className="mt-3 text-[15px] text-slate-600 max-w-2xl">
            A single place to understand outcomes, drivers, and what to do next.
          </div>

          <div className="mt-7 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <Surface className="lg:col-span-7 p-7 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-indigo-500/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />

              <div className="text-[11px] font-semibold tracking-widest uppercase text-slate-400">Revenue</div>
              <div className="mt-2 text-5xl sm:text-6xl font-semibold text-slate-900 tabular-nums">
                {perfLoading || loading ? <span className="text-slate-300">—</span> : fmtCur(totalKpi.rev)}
              </div>
              <div className="mt-5 grid grid-cols-3 gap-4">
                <Metric label="ROAS" value={perfLoading || loading ? '—' : `${fmtDec(totalKpi.roas)}x`} accent />
                <Metric label="Orders" value={perfLoading || loading ? '—' : fmt(totalKpi.tx)} />
                <Metric label="Conv. rate" value={perfLoading || loading ? '—' : fmtPct(totalKpi.cr)} />
              </div>

              <div className="mt-6">
                <Divider />
                <div className="mt-5 grid grid-cols-2 gap-4">
                  <Metric label="Ad spend" value={perfLoading || loading ? '—' : fmtCur(totalKpi.spend)} />
                  <Metric label="Sessions" value={perfLoading || loading ? '—' : fmt(totalKpi.ses)} align="right" />
                </div>
              </div>
            </Surface>

            <div className="lg:col-span-5 flex flex-col gap-6">
              <Surface className="p-6">
                <div className="text-sm font-semibold text-slate-900">Drivers</div>
                <div className="mt-2 text-[13px] text-slate-600">
                  Which channels and which funnel steps are shaping the outcome.
                </div>
                <div className="mt-5 space-y-4">
                  {channelCards.map((c) => (
                    <div key={c.name}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ToneDot tone={c.tone} />
                          <div className="text-[13px] font-semibold text-slate-800">{c.name}</div>
                        </div>
                        <div className="text-[12px] text-slate-500 tabular-nums">{fmtCur(c.rev)}</div>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${c.toneBase}`}
                          style={{ width: `${Math.max(6, Math.min(100, c.share))}%` }}
                        />
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                        <span>Share</span>
                        <span className="tabular-nums">{c.share.toFixed(0)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Surface>

              <Surface className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Trend</div>
                    <div className="text-[13px] text-slate-600 mt-1">Last 12 months revenue</div>
                  </div>
                  <div className="text-[11px] text-slate-400">{trendData ? ' ' : 'No trend data'}</div>
                </div>
                <div className="mt-4 h-[140px]">
                  {trendData ? (
                    <LineChart data={trendData} options={trendOpts} height={140} />
                  ) : (
                    <div className="h-full rounded-2xl bg-slate-50 border border-slate-100" />
                  )}
                </div>
              </Surface>
            </div>
          </div>
        </div>

        {/* KPI row (small + quiet) */}
        <Surface className="p-6 mb-10">
          <div className="text-sm font-semibold text-slate-900">Key metrics</div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            <Metric label="Revenue" value={perfLoading || loading ? '—' : fmtCur(totalKpi.rev)} accent />
            <Metric label="Spend" value={perfLoading || loading ? '—' : fmtCur(totalKpi.spend)} />
            <Metric label="ROAS" value={perfLoading || loading ? '—' : `${fmtDec(totalKpi.roas)}x`} />
            <Metric label="Sessions" value={perfLoading || loading ? '—' : fmt(totalKpi.ses)} />
            <Metric label="Orders" value={perfLoading || loading ? '—' : fmt(totalKpi.tx)} />
            <Metric label="AOV" value={perfLoading || loading ? '—' : fmtCur(totalKpi.aov)} />
          </div>
        </Surface>

        {/* Tables (from normal overview, restyled) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          <Surface className="p-6 lg:col-span-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
                <img src="/6033716.png" alt="Meta Ads" className="w-4 h-4" />
                <div className="text-sm font-semibold text-slate-900">Ads summary</div>
              </div>
              <div className="text-[12px] text-slate-500">{rangeLabel(dateRange)}</div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200/60 bg-white/60 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-white/70 border-b border-slate-200/60">
                    <tr className="text-slate-500">
                      <th className="text-left py-2 px-3 font-medium">Platform</th>
                      <th className="text-right py-2 px-2 font-medium">Sessions</th>
                      <th className="text-right py-2 px-2 font-medium">Clicks</th>
                      <th className="text-right py-2 px-2 font-medium">Cost</th>
                      <th className="text-right py-2 px-2 font-medium">Conv.</th>
                      <th className="text-right py-2 px-2 font-medium">GA4 Rev</th>
                      <th className="text-right py-2 px-2 font-medium">Revenue</th>
                      <th className="text-right py-2 px-2 font-medium">CPC</th>
                      <th className="text-right py-2 px-3 font-medium">CPS</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700">
                    {adsSummary.rows.map((r) => (
                      <tr key={r.platform} className="border-b border-slate-100/80">
                        <td className="py-2 px-3 font-semibold text-slate-800">
                          <span className="inline-flex items-center gap-2">
                            <img src={r.icon} alt="" className="w-4 h-4" />
                            {r.platform}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(r.sessions)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(r.clicks)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-rose-700">{fmtCur(r.cost)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(r.conversions)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-emerald-700">{fmtCur(r.ga4Revenue)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-amber-700">{fmtCur(r.revenue)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-indigo-700">{fmtMoney2(r.cpc)}</td>
                        <td className="py-2 px-3 text-right tabular-nums text-violet-700">{fmtMoney2(r.cps)}</td>
                      </tr>
                    ))}
                    <tr className="bg-white/70">
                      <td className="py-2 px-3 font-semibold text-slate-900">Total</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums">{fmt(adsSummary.total.sessions)}</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums">{fmt(adsSummary.total.clicks)}</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums text-rose-800">{fmtCur(adsSummary.total.cost)}</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums">{fmt(adsSummary.total.conversions)}</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums text-emerald-800">{fmtCur(adsSummary.total.ga4Revenue)}</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums text-amber-800">{fmtCur(adsSummary.total.revenue)}</td>
                      <td className="py-2 px-2 text-right font-semibold tabular-nums text-indigo-800">{fmtMoney2(adsSummary.total.cpc)}</td>
                      <td className="py-2 px-3 text-right font-semibold tabular-nums text-violet-800">{fmtMoney2(adsSummary.total.cps)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Surface>

          <Surface className="p-6">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-slate-900">Daily performance</div>
              <div className="text-[12px] text-slate-500">{dailyPerformance.windowLabel}</div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200/60 bg-white/60 overflow-hidden">
              <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-white/80 border-b border-slate-200/60">
                    <tr className="text-slate-500">
                      <th className="text-left py-2 px-3 font-medium">Date</th>
                      <th className="text-right py-2 px-2 font-medium text-emerald-700">Sessions</th>
                      <th className="text-right py-2 px-2 font-medium text-amber-700">Tx</th>
                      <th className="text-right py-2 px-2 font-medium text-violet-700">CR</th>
                      <th className="text-right py-2 px-2 font-medium text-indigo-700">Revenue</th>
                      <th className="text-right py-2 px-2 font-medium text-rose-700">
                        <span className="inline-flex items-center gap-1 justify-end w-full">
                          <img src="/google_ads-512.webp" alt="" className="w-3.5 h-3.5" />
                          Cost
                        </span>
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-blue-700">
                        <span className="inline-flex items-center gap-1 justify-end w-full">
                          <img src="/6033716.png" alt="" className="w-3.5 h-3.5" />
                          Cost
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700">
                    {dailyPerformance.rows.map((r) => (
                      <tr key={r.date} className="border-b border-slate-100/80">
                        <td className="py-2 px-3 text-slate-600">{r.date}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(r.sessions)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(r.transactions)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmtPct(r.cr)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmtCur(r.revenue)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmtCur(r.gCost)}</td>
                        <td className="py-2 px-3 text-right tabular-nums">{fmtCur(r.mCost)}</td>
                      </tr>
                    ))}
                    {dailyPerformance.rows.length === 0 && (
                      <tr>
                        <td className="py-8 px-3 text-center text-slate-400" colSpan={7}>
                          No daily data for this window.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Surface>
        </div>

        {/* Drivers: channel + funnel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <Surface className="p-6">
            <div className="text-sm font-semibold text-slate-900">Channel contribution</div>
            <div className="mt-2 text-[13px] text-slate-600">A simple view of spend and return by channel.</div>
            <div className="mt-5 space-y-4">
              {channelCards.map((c) => (
                <div key={c.name} className="rounded-2xl border border-slate-200/60 bg-white/60 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ToneDot tone={c.tone} />
                      <div className="text-[13px] font-semibold text-slate-800">{c.name}</div>
                    </div>
                    <div className="text-[12px] text-slate-500 tabular-nums">{c.share.toFixed(0)}%</div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <Metric label="Spend" value={fmtCur(c.spend)} />
                    <Metric label="Revenue" value={fmtCur(c.rev)} />
                    <Metric label="ROAS" value={`${fmtDec(c.roas)}x`} accent />
                  </div>
                </div>
              ))}
            </div>
          </Surface>

          <Surface className="p-6">
            <div className="text-sm font-semibold text-slate-900">Funnel</div>
            <div className="mt-2 text-[13px] text-slate-600">Where users drop off from intent to purchase.</div>
            <div className="mt-5 space-y-3">
              {funnel.map((s, i) => (
                <div key={s.key} className="flex items-center gap-4">
                  <div className="w-24 text-[12px] text-slate-500">{s.label}</div>
                  <div className="flex-1">
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-slate-900/80"
                        style={{ width: `${Math.max(4, s.pctOfMax)}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-20 text-right text-[12px] text-slate-600 tabular-nums">{fmt(s.value)}</div>
                  <div className="w-16 text-right text-[11px] tabular-nums text-slate-400">
                    {i > 0 && s.drop > 0 ? `-${s.drop.toFixed(0)}%` : ' '}
                  </div>
                </div>
              ))}
              {(driversLoading || loading) && <div className="text-[12px] text-slate-400 mt-2">Loading funnel…</div>}
            </div>
          </Surface>
        </div>

        {/* Insights */}
        <div className="mb-10">
          <div className="text-sm font-semibold text-slate-900">Insights</div>
          <div className="mt-2 text-[13px] text-slate-600">Three small notes to guide the next move.</div>
          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {insightCards.map((x, i) => (
              <Surface key={i} className="p-6">
                <div className="flex items-center gap-2">
                  <ToneDot tone={x.tone} />
                  <div className="text-[13px] font-semibold text-slate-900">{x.title}</div>
                </div>
                <div className="mt-3 text-[13px] leading-relaxed text-slate-600">{x.body}</div>
              </Surface>
            ))}
          </div>
        </div>

        {/* Live Today (small, not the hero) */}
        <Surface className="p-6">
          <button className="w-full flex items-center justify-between" onClick={() => setLiveOpen((v) => !v)}>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-slate-900">Live today</div>
              <div className="text-[12px] text-slate-500">{liveUpdatedAt ? liveUpdatedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
            </div>
            {liveOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Ads live (table) */}
            <div className="lg:col-span-2 rounded-2xl border border-slate-200/60 bg-white/60 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200/60 flex items-center justify-between">
                <div className="text-[12px] font-semibold text-slate-800">Ads</div>
                {liveLoading && <div className="text-[11px] text-slate-400">Updating…</div>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="bg-white/70 border-b border-slate-200/60">
                    <tr className="text-slate-500">
                      <th className="text-left py-2 px-4 font-medium">Platform</th>
                      <th className="text-right py-2 px-2 font-medium">Spend</th>
                      <th className="text-right py-2 px-2 font-medium">Clicks</th>
                      <th className="text-right py-2 px-2 font-medium">Conv.</th>
                      <th className="text-right py-2 px-2 font-medium">ROAS</th>
                      <th className="text-right py-2 px-4 font-medium">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-700">
                    {[
                      {
                        name: 'Google Ads',
                        icon: '/google_ads-512.webp',
                        spend: Number(liveGads?.cost || 0),
                        clicks: Number(liveGads?.clicks || 0),
                        conv: Number(liveGads?.conversions || 0),
                        revenue: Number(liveGads?.conversion_value ?? liveGads?.revenue ?? 0),
                        roas: Number(
                          liveGads?.roas ??
                            (Number(liveGads?.cost || 0) > 0 ? Number(liveGads?.conversion_value ?? liveGads?.revenue ?? 0) / Number(liveGads?.cost || 1) : 0)
                        ),
                      },
                      {
                        name: 'Meta Ads',
                        icon: '/6033716.png',
                        spend: Number(liveMeta?.spend || 0),
                        clicks: Number(liveMeta?.clicks || 0),
                        conv: Number(liveMeta?.conversions || 0),
                        revenue: Number(liveMeta?.conversion_value || 0),
                        roas: Number(
                          liveMeta?.roas ??
                            (Number(liveMeta?.spend || 0) > 0 ? Number(liveMeta?.conversion_value || 0) / Number(liveMeta?.spend || 1) : 0)
                        ),
                      },
                    ].map((r) => (
                      <tr key={r.name} className="border-b border-slate-100/80">
                        <td className="py-2 px-4 font-semibold text-slate-800">
                          <span className="inline-flex items-center gap-2">
                            <img src={r.icon} alt="" className="w-4 h-4" />
                            {r.name}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-rose-700">{fmtCur(r.spend)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(r.clicks)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(r.conv)}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-slate-900">{`${fmtDec(r.roas)}x`}</td>
                        <td className="py-2 px-4 text-right tabular-nums text-amber-700">{fmtCur(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* GA4 live (compact table) */}
            <div className="rounded-2xl border border-slate-200/60 bg-white/60 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200/60 flex items-center justify-between">
                <div className="text-[12px] font-semibold text-slate-800 inline-flex items-center gap-2">
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
                  GA4
                </div>
                {liveLoading && <div className="text-[11px] text-slate-400">Updating…</div>}
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4">
                  <Metric label="Sessions" value={fmt(liveGa4?.sessions)} />
                  <Metric label="Users" value={fmt(liveGa4?.users)} align="right" />
                  <Metric label="Tx" value={fmt(liveGa4?.transactions)} />
                  <Metric label="Revenue" value={fmtCur(liveGa4?.revenue)} align="right" accent />
                </div>
                {liveOpen && (
                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4">
                    <Metric label="Bounce" value={fmtPct(liveGa4?.bounceRate)} />
                    <Metric label="Avg. dur." value={`${Math.round(Number(liveGa4?.avgSessionDuration || 0))}s`} align="right" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 text-[11px] text-slate-400">
            Live is intentionally small. Use it to sanity-check today, not to run the whole week.
          </div>
        </Surface>
      </div>
    </div>
  );
}

